import {
  IconArrowRight,
  IconHeart,
  IconHeartFilled,
  IconMoon,
  IconPhotoFilled,
  IconPlaneDeparture,
  IconToolsKitchen2,
} from "@tabler/icons-react";
import { useState } from "react";
import { Link } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useLocaleContext } from "src/context/locale-context";
import { favoritesActions, selectIsFavorited } from "src/store/favorites";
import classes from "./CharterDealCard.module.css";
import { getCharterDealImage } from "./charterDealImage";
import { getCharterText } from "./charterDealsText";

const BADGE_CLASS = {
  rose: classes.badgeRose,
  sky: classes.badgeSky,
  mint: classes.badgeMint,
  amber: classes.badgeAmber,
  violet: classes.badgeViolet,
};

export function CharterDealCard({ deal }) {
  const { getStringRes, currencySymbol } = useLocaleContext();
  const dispatch = useDispatch();
  const [imgError, setImgError] = useState(false);
  const imageUrl = getCharterDealImage(deal);
  const hasImage = !imgError && !!imageUrl;
  const isExternal = deal.link?.startsWith("http");
  const favoriteId = `charter-${deal.id}`;
  const isFavorite = useSelector((state) => selectIsFavorited(state, favoriteId));

  const onToggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(favoritesActions.toggleFavorite({ id: favoriteId, type: "charter", data: deal }));
    if (!isFavorite) {
      toast.success(getStringRes("favorites.toast.saved"));
    }
  };

  const infoContent = (
    <>
      <div className={classes.imageWrap}>
        {hasImage ? (
          <img
            src={imageUrl}
            alt={deal.title}
            className={classes.image}
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className={classes.imageFallback}>
            <IconPhotoFilled size="35%" color="var(--ui-4)" />
          </div>
        )}
        {deal.badge && (
          <span className={`${classes.badge} ${BADGE_CLASS[deal.badgeTone] || classes.badgeSky}`}>
            {deal.badge}
          </span>
        )}
      </div>

      <div className={classes.body}>
        <h3 className={classes.name}>{deal.title}</h3>

        {Number.isFinite(deal.nights) && (
          <div className={classes.metaRow}>
            <IconMoon size={16} stroke={1.75} />
            <span>
              {deal.nights} {getCharterText(getStringRes, "charter.deals.nights")}
            </span>
          </div>
        )}
        {deal.meal && (
          <div className={classes.metaRow}>
            <IconToolsKitchen2 size={16} stroke={1.75} />
            <span>{deal.meal}</span>
          </div>
        )}
        {deal.departure && (
          <div className={classes.metaRow}>
            <IconPlaneDeparture size={16} stroke={1.75} />
            <span>{deal.departure}</span>
          </div>
        )}

        <hr className={classes.divider} />

        <div className={classes.priceRow}>
          {Number.isFinite(deal.price) ? (
            <>
              <span className={classes.priceLabel}>
                {getCharterText(getStringRes, "charter.deals.priceFrom")}
              </span>
              <div className={classes.priceLine}>
                <span className={classes.priceAmount}>
                  {deal.price.toFixed(0)} {currencySymbol}
                </span>
                <span className={classes.pricePerPerson}>
                  {getCharterText(getStringRes, "charter.deals.perPerson")}
                </span>
              </div>
            </>
          ) : (
            <span className={classes.priceNone}>{getStringRes("cheap.hotels.price.none")}</span>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className={classes.card}>
      {/* A <button> can't nest inside an <a>/<Link>, so the CTA sits as a sibling after the
          info block rather than the whole card being one giant link. */}
      {!deal.link ? (
        <div className={classes.cardLink}>{infoContent}</div>
      ) : isExternal ? (
        <a href={deal.link} target="_blank" rel="noopener noreferrer" className={classes.cardLink}>
          {infoContent}
        </a>
      ) : (
        <Link to={deal.link} className={classes.cardLink}>
          {infoContent}
        </Link>
      )}

      <button
        type="button"
        className={classes.favoriteButton}
        aria-label={getStringRes("cheap.hotels.favorite")}
        onClick={onToggleFavorite}>
        {isFavorite ? (
          <IconHeartFilled size={16} color="#ff2965" />
        ) : (
          <IconHeart size={16} stroke={1.75} />
        )}
      </button>

      <div className={classes.ctaWrap}>
        <Link
          to={!isExternal ? deal.link || "#" : "#"}
          onClick={(e) => {
            if (!deal.link) e.preventDefault();
            if (isExternal) {
              e.preventDefault();
              window.open(deal.link, "_blank", "noopener,noreferrer");
            }
          }}
          className={`${classes.ctaButton} ${!deal.link ? classes.ctaButtonDisabled : ""}`}>
          {getCharterText(getStringRes, "charter.deals.view")}
          <IconArrowRight size={16} stroke={2} />
        </Link>
      </div>
    </div>
  );
}
